import "./AccountsMaps.css";
import "@xyflow/react/dist/style.css";

import {
  addEdge,
  Background,
  type Connection,
  Controls,
  type Edge,
  type Node,
  type NodeTypes,
  Panel,
  ReactFlow,
  useEdgesState,
  useNodesState,
} from "@xyflow/react";
import { useCallback, useEffect, useMemo, useState } from "react";

import {
  GroupNode,
  PreviousNode,
  RootNode,
  ScrollableNode,
} from "../components";
import { parseNodeAndEdges } from "../parser";
import { type AccessMapSuccessResponse, type NodeData } from "../types";
import { TriggerSource } from "@repo/api/schemas/policiesInventory.schema";
import InventorySidePanel from "../../PoliciesForm/InventorySidePanel/InventorySidePanel";
import { useTheme } from "@repo/mfe-core";
import {
  PermissionProvider,
  usePermissionContext,
} from "../../../context/permissionContext";
import useUserPermissions, {
  PERMISSION_KEYS,
} from "../../../hooks/useUserPermissions";

const nodeTypes: NodeTypes = {
  rootNode: RootNode,
  groupNode: GroupNode,
  scrollableNode: ScrollableNode,
};

function AccountsMapContent({
  id,
  data,
  previousNodes,
  onPreviousNodeClick,
  onMakeItFocusedNodeClick,
  closeSearchSidePanel,
  closeCriticalHighRiskSidePanel,
}: {
  id: string;
  data: AccessMapSuccessResponse;
  previousNodes: NodeData[];
  onPreviousNodeClick: () => void;
  onMakeItFocusedNodeClick: (node: NodeData) => void;
  closeSearchSidePanel: () => void;
  closeCriticalHighRiskSidePanel: () => void;
}) {
  const params = new URLSearchParams(location.search);
  const variant = params.get("variant") as TriggerSource;
  const { theme } = useTheme();

  const colorMode = useMemo(() => (theme ? "dark" : "light"), [theme]);

  const onNodeClick = useCallback(
    (node: NodeData) => {
      const { id: assetId, displayName, source, label } = node;

      if (["Account", "Machine", "Credential"].includes(label)) {
        setSidecarMetaData({
          id: assetId,
          assetName: displayName,
          connectionName: "",
          source,
          activeTabId: "1",
          displayName,
        });
        setIsSidecarOpen(true);
        closeSearchSidePanel();
        closeCriticalHighRiskSidePanel();
      }
    },
    [closeCriticalHighRiskSidePanel, closeSearchSidePanel],
  );

  const { nodes: initialNodes, edges: initialEdges } = useMemo(
    () => parseNodeAndEdges(id, data, onMakeItFocusedNodeClick, onNodeClick),
    [data, id, onMakeItFocusedNodeClick, onNodeClick],
  );
  const { setIsPermissionEnabled } = usePermissionContext();

  const [isSidecarOpen, setIsSidecarOpen] = useState(false);
  // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
  const [sidecarMetaData, setSidecarMetaData] = useState({
    id: "",
    assetName: "",
    connectionName: "",
    source: "",
    displayName: "",
    activeTabId: "1",
  });

  const [nodes, setNodes, onNodesChange] = useNodesState(
    initialNodes as unknown as Node[],
  );
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const { permission: assetOwnership } = useUserPermissions(
    PERMISSION_KEYS.ISPM_FINDINGS_MANAGE,
  );

  useEffect(() => {
    setIsPermissionEnabled(assetOwnership?.enabled ?? false);
  }, [assetOwnership, setIsPermissionEnabled]);

  useEffect(() => {
    if (initialNodes.length > 0) {
      setNodes(initialNodes as unknown as Node[]);
      setEdges(initialEdges);
    }
  }, [initialNodes, initialEdges, setNodes, setEdges]);

  const onConnect = useCallback(
    (connection: Connection) =>
      setEdges((eds: Edge[]) => addEdge(connection, eds)),
    [setEdges],
  );

  const closeSidecar = () => {
    setIsSidecarOpen(false);
    setSidecarMetaData({
      id: "",
      assetName: "",
      connectionName: "",
      source: "",
      displayName: "",
      activeTabId: "1",
    });
  };

  return (
    <>
      <ReactFlow
        colorMode={colorMode}
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        nodeTypes={nodeTypes}
        onConnect={onConnect}
        nodesDraggable={false}
        fitView
      >
        <Controls
          orientation="horizontal"
          position="top-center"
          showInteractive={false}
        />
        <Background gap={24} />
        <Panel position="top-left">
          {previousNodes.length > 0 && (
            <PreviousNode
              data={previousNodes[previousNodes.length - 1]}
              onNodeClick={onPreviousNodeClick}
            />
          )}
        </Panel>
      </ReactFlow>
      <InventorySidePanel
        isSidecarOpen={isSidecarOpen}
        closeSidecar={closeSidecar}
        sidecarMetaData={sidecarMetaData}
        variant={variant}
      />
    </>
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export default function AccountsMap(props: any) {
  return (
    <PermissionProvider>
      <AccountsMapContent {...props} />
    </PermissionProvider>
  );
}
